import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import AddTask from '@mui/icons-material/AddTask';
import Close from '@mui/icons-material/Close';

import { useForm } from '../../../hooks/useForm';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { TablePermissionsUsers } from './TablePermissionsUsers';
import { useDispatch } from 'react-redux';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useManageUsers } from '../hooks/useManageUsers';
import { ActionButtons } from '../../../components/others/ActionButtons';
import { GenericAutocomplete } from '../../../components/others/GenericAutocomplete';

export const ModalFormsEditCreate = ({ data, isCreate = false, open, setOpen }) => {
    const [{ userSession }] = useDataAuth();
    const [UsersManager] = useManageUsers();
    const dispatch = useDispatch();
    const {
        listCompaniesPermissions,
        listGroupPermissions,
        isLoadingPermissions,
        listAllPermissions,
        DeletePermission,
        getCompaniesAcces,
        getGroupsAcces,
        AddPermissions,
        SaveData,
        t
    } = UsersManager;

    const [{ user_nameError, nameError, last_nameError }, handleErrorInput] = useErrorInputs({});

    const [{ id_user, user_name, name, last_name, status }, handleInputChange] = useForm({
        id_user: data?.id_user || 0,
        user_name: data?.user_name,
        name: data?.name,
        last_name: data?.last_name,
        status: data?.status || 1
    });

    const [idCompany, setIdCompany] = useState('');
    const [idPermission, setIdPermission] = useState('');

    const handleSubmit = (e) => {
        console.log('handleSubmit');
        e.preventDefault();
        let isErrors = false;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!user_name && isCreate) {
            handleErrorInput("user_nameError", {
                isError: true,
                messageError: t("validation.requiredField")
            });
            isErrors = true;
        }
        else if (!emailRegex.test(user_name) && isCreate) {
            handleErrorInput("user_nameError", {
                'isError': true,
                'messageError': t("validation.invalidFieldValue")
            });
            isErrors = true;
        }
        else {
            handleErrorInput("user_nameError", {
                'isError': false,
                'messageError': ''
            });
        }

        if (!name) {
            handleErrorInput("nameError", {
                isError: true,
                messageError: t("validation.requiredField")
            });
            isErrors = true;
        }
        else {
            handleErrorInput("nameError", {
                'isError': false,
                'messageError': ''
            });
        }

        if (!last_name) {
            handleErrorInput("last_nameError", {
                isError: true,
                messageError: t("validation.requiredField")
            });
            isErrors = true;
        }
        else if (last_name.trim().length < 2) {
            isErrors = true;
            handleErrorInput("last_nameError", {
                'isError': true,
                'messageError': t("validation.minLength2")
            });
            isErrors = true;
        }
        else {
            handleErrorInput("last_nameError", {
                'isError': false,
                'messageError': ''
            });
        }
        if (isErrors) {
            console.log({ isErrors });
            return false;
        }
        SaveChanges();
    }

    useEffect(() => {
        dispatch(getCompaniesAcces(userSession.token));
    }, [dispatch, getCompaniesAcces, userSession.token]);

    const handleChangeEmpresa = (e) => {
        setIdCompany(e.target.value);
        setIdPermission('');
        dispatch(getGroupsAcces(userSession.token, e.target.value));
    }

    const handleAddPermission = () => {
        let dto = {
            id_user: id_user,
            id_company: idCompany,
            user_access_type: 'group',
            id_access: idPermission
        };
        AddPermissions(userSession.token, dto, id_user, isCreate);
        setIdCompany('');
        setIdPermission('');
    }

    const SaveChanges = () => {
        let dto = { id_user, user_name, name, last_name, status };
        SaveData(userSession.token, dto, isCreate);
        setOpen(false);
    }

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth='sm'
            PaperProps={{
                style: { height: '90%' },
            }}
        >
            <DialogTitle>
                <Typography variant='h5' component='h5'>
                    {t("user.userFormTitle")}
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ marginTop: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={{xs:12, sm:6}}>
                            <TextField
                                error={user_nameError?.isError}
                                color={(user_nameError?.isError) && 'error'}
                                helperText={user_nameError?.messageError || ''}
                                required
                                fullWidth
                                label={t('user.userName')}
                                placeholder={t('user.userNamePlaceholder')}
                                variant='outlined'
                                name='user_name'
                                size='small'
                                disabled={!isCreate}
                                value={user_name}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{xs:12, sm:6}}>
                            <TextField
                                error={nameError?.isError}
                                color={(nameError?.isError) && 'error'}
                                helperText={nameError?.messageError || ''}
                                required
                                fullWidth
                                label={t('user.name')}
                                InputLabelProps={{ shrink: true }}
                                size='small'
                                variant='outlined'
                                name='name'
                                value={name}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs:12, sm:6}}>
                            <TextField
                                error={last_nameError?.isError}
                                color={(last_nameError?.isError) && 'error'}
                                helperText={last_nameError?.messageError || ''}
                                required
                                fullWidth
                                label={t('user.lastName')}
                                InputLabelProps={{ shrink: true }}
                                size='small'
                                variant='outlined'
                                name='last_name'
                                value={last_name}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs:12, sm:6}}>
                            <FormControlLabel
                                labelPlacement="top"
                                control={
                                    <Switch
                                        id='status'
                                        name='status'
                                        onChange={(e) => handleInputChange(
                                            {
                                                target: {
                                                    name: e.target.name,
                                                    value: e.target.checked ? 1 : 0
                                                }
                                            }
                                        )}
                                        checked={status === 1}
                                    />
                                }
                                label={t('user.status')}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ marginTop: 5, marginBottom: 2 }}>
                        <Typography variant='h6' component='h6'>
                            {t("user.grantedPermissions")}
                        </Typography>
                        <Grid sx={{ marginTop: 5 }} container spacing={2}>
                            <Grid size={{xs:12, sm:5}}>
                                <GenericAutocomplete
                                    options={listCompaniesPermissions}
                                    value={idCompany}
                                    label={t('user.company')}
                                    name="InputCompanies"
                                    onChange={handleChangeEmpresa}
                                    disableCloseOnSelect={false}
                                />
                            </Grid>
                            <Grid size={{xs:12, sm:5}}>
                                <GenericAutocomplete
                                    options={listGroupPermissions}
                                    value={idPermission}
                                    label={t('user.permission')}
                                    name="InputPermissions"
                                    onChange={(e) => setIdPermission(e.target.value)}
                                    disabled={idCompany === ''}
                                    disableCloseOnSelect={false}
                                />
                            </Grid>
                            <Grid size={{xs:12, sm:2}}>
                                <Tooltip title={t("add")}>
                                    <IconButton
                                        aria-label="close"
                                        onClick={handleAddPermission}
                                        disabled={idPermission === '' || idCompany === ''}
                                        sx={{
                                            color: 'blue',
                                        }}
                                    >
                                        <AddTask />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ marginTop: 5, marginBottom: 2 }}>
                        <TablePermissionsUsers
                            isLoadingPermissions={isLoadingPermissions}
                            listAllPermissions={listAllPermissions}
                            DeletePermission={DeletePermission}
                            t={t}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <ActionButtons
                            onCancel={() => setOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}